// StaffList.js - Fixed select all and display
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Select, Tag, Spin, message, Button, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { request } from "../../share/request";
import { getCurrentUser } from "../../share/getProfile";
import { linkPath } from "../../share/helper";

const { Option } = Select;

const StaffList = function(props) {
  var selectedStaff = props.selectedStaff || [];
  var onChange = props.onChange;
  var departmentCode = props.departmentCode || null;
  var placeholder = props.placeholder || "ជ្រើសរើសបុគ្គលិក...";
  var multiple = props.multiple !== undefined ? props.multiple : true;
  var orgCode = props.orgCode || null;
  var allowClear = props.allowClear !== undefined ? props.allowClear : true;
  var maxTagCount = props.maxTagCount || 'responsive';
  var showAvatar = props.showAvatar !== undefined ? props.showAvatar : true;
  var showSelectAll = props.showSelectAll !== undefined ? props.showSelectAll : true;

  var getProfile = getCurrentUser();
  var _useState = useState([]);
  var staff = _useState[0];
  var setStaff = _useState[1];
  var _useState2 = useState(false);
  var loading = _useState2[0];
  var setLoading = _useState2[1];
  var _useState3 = useState(null);
  var error = _useState3[0];
  var setError = _useState3[1];
  var _useState4 = useState('');
  var searchText = _useState4[0];
  var setSearchText = _useState4[1];
  var searchTimeoutRef = useRef(null);

  var getImageUrl = useCallback(function(imagePath) {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    if (imagePath.startsWith('/')) {
      return linkPath + imagePath;
    }
    return linkPath.endsWith('/') ? linkPath + imagePath : linkPath + '/' + imagePath;
  }, []);

  var fetchStaff = useCallback(async function(searchValue) {
    var searchVal = searchValue || "";
    setLoading(true);
    setError(null);
    
    try {
      var param = new URLSearchParams();
      param.append("search", searchVal);
      param.append("org_code", orgCode || getProfile.org_code);

      if (departmentCode) {
        if (departmentCode.indexOf(',') !== -1) {
          param.append("dept_codes", departmentCode);
        } else {
          param.append("dept_code", departmentCode);
        }
      }

      var res = await request("lettersjoiner?" + param.toString(), "get", {});
      
      if (res && res.api_server_list_joiner && Array.isArray(res.api_server_list_joiner)) {
        var staffList = res.api_server_list_joiner
          .filter(function(item) { return item; })
          .map(function(item) {
            return {
              user_id: item.id,
              username: item.staff_id_number || '',
              name: item.staffname_kh1 || item.staffname_en || 'Unknown',
              position: item.position_kh || item.position_en || '',
              department: item.department_name || '',
              image: item.image || null,
              image_url: item.image ? getImageUrl(item.image) : null,
              is_active: item.is_active || 1,
            };
          });
        
        setStaff(staffList);
        return staffList;
      } else {
        setStaff([]);
        return [];
      }
    } catch (err) {
      console.error("Error fetching staff:", err);
      setError("មិនអាចទាញយកបញ្ជីបុគ្គលិកបានទេ");
      message.error("មិនអាចទាញយកបញ្ជីបុគ្គលិកបានទេ");
      return [];
    } finally {
      setLoading(false);
    }
  }, [getProfile.org_code, orgCode, departmentCode, getImageUrl]);

  useEffect(function() {
    fetchStaff("");
  }, [fetchStaff]);

  var handleSearch = function(value) {
    setSearchText(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(function() {
      fetchStaff(value);
    }, 300);
  };

  useEffect(function() {
    return function() {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  var handleChange = function(selectedValues) {
    if (multiple) {
      var values = Array.isArray(selectedValues) ? selectedValues : (selectedValues ? [selectedValues] : []);
      onChange(values);
    } else {
      onChange(selectedValues ? [selectedValues] : []);
    }
  };

  // FIXED: Select all logic
  var handleSelectAll = function(e) {
    e.stopPropagation();
    e.preventDefault();
    
    if (isAllSelected) {
      // Deselect all
      onChange([]);
    } else {
      // Select all
      var allIds = staff.map(function(person) { return person.user_id; });
      onChange(allIds);
    }
  };

  var handleDeselect = function(value) {
    if (multiple) {
      var newSelected = selectedStaff.filter(function(id) { return id !== value; });
      onChange(newSelected);
    } else {
      onChange([]);
    }
  };

  var getStaffMember = function(staffId) {
    return staff.find(function(person) { return person.user_id === staffId; });
  };

  // FIXED: Check if all staff are selected
  var isAllSelected = staff.length > 0 && 
    staff.every(function(person) {
      return selectedStaff.indexOf(person.user_id) !== -1;
    });

  return React.createElement('div', { style: { marginBottom: 16 } },
    React.createElement(Select, {
      mode: multiple ? "multiple" : undefined,
      value: multiple ? selectedStaff : (selectedStaff[0] || undefined),
      onChange: handleChange,
      onDeselect: handleDeselect,
      onSearch: handleSearch,
      placeholder: placeholder,
      loading: loading,
      allowClear: allowClear,
      showSearch: true,
      filterOption: false,
      maxTagCount: maxTagCount,
      maxTagPlaceholder: function(omittedValues) {
        return React.createElement(Tag, null, '+' + omittedValues.length + ' នាក់ទៀត');
      },
      style: { width: '100%' },
      notFoundContent: loading 
        ? React.createElement(Spin, { size: "small" })
        : (searchText ? 'រកមិនឃើញបុគ្គលិក' : 'វាយបញ្ចូលដើម្បីស្វែងរក'),
      dropdownRender: function(menu) {
        return React.createElement('div', null,
          multiple && showSelectAll && staff.length > 0 && React.createElement('div', {
            style: { 
              padding: '8px 12px', 
              borderBottom: '1px solid #f0f0f0', 
              cursor: 'pointer',
              userSelect: 'none'
            },
            onClick: handleSelectAll,
            onMouseDown: function(e) { e.preventDefault(); }
          },
            React.createElement('label', { 
              style: { 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center',
                margin: 0
              }
            },
              React.createElement('input', { 
                type: "checkbox", 
                checked: isAllSelected, 
                readOnly: true, 
                style: { marginRight: 8, cursor: 'pointer' },
                onClick: function(e) { e.stopPropagation(); }
              }),
              React.createElement('span', null, 
                isAllSelected ? 'ដកចេញទាំងអស់' : 'ជ្រើសរើសទាំងអស់'
              ),
              React.createElement('span', { 
                style: { fontSize: '12px', color: '#999', marginLeft: 8 }
              }, '(' + staff.length + ' នាក់)')
            )
          ),
          React.createElement('div', { style: { maxHeight: '300px', overflowY: 'auto' } }, menu)
        );
      },
      tagRender: function(props) {
        var value = props.value;
        var closable = props.closable;
        var onClose = props.onClose;
        var staffMember = getStaffMember(value);
        var displayName = staffMember ? staffMember.name : String(value || '');
        var imageUrl = staffMember ? staffMember.image_url : null;
        
        return React.createElement(Tag, {
          closable: closable,
          onClose: onClose,
          style: { marginRight: 3, marginBottom: 2, display: 'inline-flex', alignItems: 'center', gap: '4px' }
        },
          showAvatar && React.createElement(Avatar, { 
            size: 18, 
            src: imageUrl,
            icon: !imageUrl ? React.createElement(UserOutlined) : null,
            style: { fontSize: '10px' }
          }),
          React.createElement('span', null, displayName)
        );
      }
    },
      staff.map(function(person) {
        var imageUrl = person.image ? getImageUrl(person.image) : null;
        
        return React.createElement(Option, { 
          key: person.user_id, 
          value: person.user_id,
          label: person.name
        },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
            showAvatar && React.createElement(Avatar, { 
              size: 30, 
              src: imageUrl,
              icon: !imageUrl ? React.createElement(UserOutlined) : null
            }),
            React.createElement('div', null,
              React.createElement('div', { style: { fontWeight: '500' } }, person.name),
              person.position && React.createElement('div', { style: { fontSize: '12px', color: '#666' } }, person.position),
              person.department && React.createElement('div', { style: { fontSize: '11px', color: '#999' } }, person.department)
            )
          )
        );
      })
    ),

    multiple && selectedStaff.length > 0 && React.createElement('div', { 
      style: { marginTop: 8, fontSize: '13px', color: '#666' }
    },
      React.createElement('span', null, 
        'បានជ្រើសរើស: ' + selectedStaff.length + ' នាក់'
      ),
      React.createElement(Button, { 
        type: "link", 
        size: "small", 
        onClick: function() { onChange([]); },
        style: { marginLeft: 8 }
      }, 'សម្អាត')
    ),

    error && React.createElement('div', { 
      style: { color: '#ff4d4f', marginTop: 8, fontSize: '12px' }
    }, error)
  );
};

export default StaffList;